import { formatInTimeZone } from 'date-fns-tz';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// Deutsche Zeitzone - alle Uhrzeit-Anzeigen laufen hierueber
export const BERLIN_TZ = 'Europe/Berlin';

/**
 * Stellt sicher, dass ein ISO-String als UTC interpretiert wird.
 * Backend liefert Zeitstempel in UTC. Fehlt der UTC-Marker (Z oder +00:00),
 * wird er ergaenzt, damit new Date() den Wert nicht als Lokalzeit missversteht.
 */
function ensureUtc(isoString) {
  if (!isoString) return null;
  const s = String(isoString);
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  return hasTz ? s : s + 'Z';
}

/**
 * Formatiert einen UTC-Zeitstempel als Uhrzeit in deutscher Zeit, z.B. "07:41".
 * Ergebnis ist auf jedem Geraet gleich, egal welche Zeitzone es hat.
 */
export function formatTimeBerlin(isoString) {
  const normalized = ensureUtc(isoString);
  if (!normalized) return '--:--';
  try {
    return formatInTimeZone(new Date(normalized), BERLIN_TZ, 'HH:mm');
  } catch {
    return '--:--';
  }
}

/**
 * Formatiert einen UTC-Zeitstempel als Datum+Uhrzeit in deutscher Zeit,
 * z.B. "26.08.2026, 07:41".
 * 
 * @param {string} isoString - UTC-Zeitstempel vom Backend
 * @param {string} fmt - date-fns Formatmuster (Default: 'dd.MM.yyyy, HH:mm')
 * @param {string} fallback - Anzeige bei fehlendem/ungueltigem Wert (Default: '--')
 */
export function formatDateTimeBerlin(isoString, fmt = 'dd.MM.yyyy, HH:mm', fallback = '--') {
  const normalized = ensureUtc(isoString);
  if (!normalized) return fallback;
  try {
    return formatInTimeZone(new Date(normalized), BERLIN_TZ, fmt);
  } catch {
    return fallback;
  }
}

/**
 * Formatiert ein Datum robust fuer die Anzeige.
 * Bei leerem, null oder ungueltigem Wert wird der Fallback zurueckgegeben,
 * statt die Anzeige abstuerzen zu lassen.
 *
 * Fuer reine Datums-Strings gedacht (z.B. "2026-08-11"). Fuer die
 * Zeitzonen-Umrechnung von Zeitstempeln bitte formatTimeBerlin /
 * formatDateTimeBerlin verwenden.
 *
 * @param {string} value - Datums-String aus dem Backend
 * @param {string} fmt - date-fns Formatmuster, z.B. 'd. MMM yyyy'
 * @param {string} fallback - Anzeige bei fehlendem/ungueltigem Wert
 */
export function safeFormatDate(value, fmt, fallback = '–') {
  if (!value) return fallback;
  try {
    return format(parseISO(value), fmt, { locale: de });
  } catch {
    return fallback;
  }
}

/**
 * Liefert ein Datum als 'YYYY-MM-DD' in der LOKALEN Zeitzone.
 *
 * Bewusst nicht toISOString(): das rechnet in UTC und liefert in
 * Deutschland zwischen 00:00 und 02:00 Uhr den Vortag.
 * Für Geschäftsdaten (Gültig-ab, Buchungsdatum) zählt das lokale Datum.
 */
export function toIsoLocal(date) {
  const p = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}